import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./login.scss";
import { getApiInstance } from "../../utils/api";
import Input from "../../components/common/Inputs/Input/Input";
import Button from "../../components/common/Button/Button";
import { useTranslation } from "react-i18next";
import Logo from "../../assets/wlc-logo-simple.png";

import {
    FaFileAlt,
    FaUsersCog,
    FaIndustry,
    FaBalanceScale,
    FaFolderOpen,
    FaCalculator,
    FaArrowLeft,
} from "react-icons/fa";
import { TbUserSquareRounded } from "react-icons/tb";
import InputCheckbox from "../../components/common/Inputs/InputCheckbox/InputCheckbox";
import { FaArrowRightToBracket } from "react-icons/fa6";

const Login = ({ onLogin }) => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const [showLoginForm, setShowLoginForm] = useState(false);
    const { t } = useTranslation();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        try {
            const api = getApiInstance();
            const response = await api.post("/api/Auth", {
                email: username,
                password: password,
            });

            if (response.data && response.data.token) {
                onLogin(response.data.token);
                navigate("/dashboard");
            }
        } catch (err) {
            console.error("Error en login:", err);
            setError(t("login.error"));
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1024);
            if (window.innerWidth > 1024) {
                setShowLoginForm(false);
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <>
            {isMobile && !showLoginForm && (
                <div className="login-page login-page--mobile-welcome">
                    <div className="login-page__mobile-content">
                        <div className="login-page__left__logo">
                            <img
                                src={Logo}
                                alt="Waste Life Cycle Logo"
                                className="login-page__left__logo__img"
                            />
                            <h1 className="login-page__left__logo__text">
                                Waste Life Cycle
                            </h1>
                        </div>

                        <div className="login-page__left__welcome">
                            <h2>Tu plataforma de gestión de residuos</h2>
                            <h3>
                                La solución integral para gestionar tus residuos
                                de forma eficiente
                            </h3>
                            <div className="login-page__left__welcome__features">
                                <div className="login-page__left__welcome__features__feature">
                                    <FaFileAlt size={24} />
                                    <p>Informes</p>
                                </div>
                                <div className="login-page__left__welcome__features__feature">
                                    <FaUsersCog size={24} />
                                    <p>Gestores</p>
                                </div>
                                <div className="login-page__left__welcome__features__feature">
                                    <FaIndustry size={24} />
                                    <p>Centros de Producción</p>
                                </div>
                                <div className="login-page__left__welcome__features__feature">
                                    <FaBalanceScale size={24} />
                                    <p>Cumplimiento Legal</p>
                                </div>
                                <div className="login-page__left__welcome__features__feature">
                                    <FaFolderOpen size={24} />
                                    <p>Gestión documental</p>
                                </div>
                                <div className="login-page__left__welcome__features__feature">
                                    <FaCalculator size={24} />
                                    <p>Control de Costes</p>
                                </div>
                            </div>

                            <Button
                                variant="filled"
                                endIcon={<FaArrowRightToBracket />}
                                iconAlign="right"
                                fullWidth={true}
                                onClick={() => setShowLoginForm(true)}
                            >
                                Acceder
                            </Button>

                            <a
                                href="https://wastelifecycle.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="login-page__manual-link"
                            >
                                Ver manual de uso
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {isMobile && showLoginForm && (
                <div className="login-page login-page--mobile-form">
                    <div className="login-page__mobile-content">
                        <button
                            className="login-page__back-btn"
                            onClick={() => setShowLoginForm(false)}
                        >
                            <FaArrowLeft />
                            Volver
                        </button>

                        <div className="login-form">
                            <div className="login-form__header">
                                <TbUserSquareRounded
                                    size={56}
                                    color="#646266"
                                />
                                <h1 className="login-form__title">
                                    Inicia sesión para continuar
                                </h1>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="login-form__inputs">
                                    <Input
                                        name="email"
                                        type="email"
                                        label="Email"
                                        placeholder="Ex.: example@email.com"
                                        value={username}
                                        onChange={(e) =>
                                            setUsername(e.target.value)
                                        }
                                    />
                                    <Input
                                        name="password"
                                        type="password"
                                        label="Contraseña"
                                        placeholder="Ex.: 123456"
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(e.target.value)
                                        }
                                    />
                                </div>
                                <div className="login-form__options">
                                    <InputCheckbox
                                        name="rememberMe"
                                        id="rememberMe"
                                        label="Mantener sesión iniciada"
                                        spacing="4px"
                                        checked={rememberMe}
                                        onChange={(e) => {
                                            console.log(
                                                "Checkbox clicked:",
                                                e.target.checked
                                            );
                                            setRememberMe(e.target.checked);
                                        }}
                                    />
                                </div>
                                {error && (
                                    <div className="login-form__error">
                                        {error}
                                    </div>
                                )}
                                <Button
                                    variant="filled"
                                    fullWidth={true}
                                    type="submit"
                                >
                                    Iniciar sesión
                                </Button>
                                <a
                                    href="/forgot-password"
                                    className="login-form__forgot"
                                >
                                    ¿Olvidaste tu contraseña?
                                </a>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {!isMobile && (
                <div className="login-page">
                    <div className="login-page__left">
                        <div className="login-page__left__logo">
                            <img
                                src={Logo}
                                alt="Waste Life Cycle Logo"
                                className="login-page__left__logo__img"
                            />
                            <h1 className="login-page__left__logo__text">
                                Waste Life Cycle
                            </h1>
                        </div>

                        <div className="login-page__left__welcome">
                            <h2>Tu plataforma de gestión de residuos</h2>
                            <h3>
                                La solución integral para gestionar tus residuos
                                de forma eficiente
                            </h3>

                            <div className="login-page__left__welcome__features">
                                <div className="login-page__left__welcome__features__feature">
                                    <FaFileAlt size={28} />
                                    <p>Informes</p>
                                </div>
                                <div className="login-page__left__welcome__features__feature">
                                    <FaUsersCog size={28} />
                                    <p>Gestores</p>
                                </div>
                                <div className="login-page__left__welcome__features__feature">
                                    <FaIndustry size={28} />
                                    <p>Centros de Producción</p>
                                </div>
                                <div className="login-page__left__welcome__features__feature">
                                    <FaBalanceScale size={28} />
                                    <p>Cumplimiento Legal</p>
                                </div>
                                <div className="login-page__left__welcome__features__feature">
                                    <FaFolderOpen size={28} />
                                    <p>Gestión documental</p>
                                </div>
                                <div className="login-page__left__welcome__features__feature">
                                    <FaCalculator size={28} />
                                    <p>Control de Costes</p>
                                </div>
                            </div>

                            <a
                                href="https://wastelifecycle.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="login-page__manual-link"
                            >
                                Ver manual de uso
                            </a>
                        </div>
                    </div>

                    <div className="login-page__right">
                        <div className="login-form">
                            <div className="login-form__header">
                                <TbUserSquareRounded
                                    size={56}
                                    color="#646266"
                                />
                                <h1 className="login-form__title">
                                    Inicia sesión para continuar
                                </h1>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="login-form__inputs">
                                    <Input
                                        name="email"
                                        type="email"
                                        label="Email"
                                        placeholder="Ex.: example@email.com"
                                        value={username}
                                        onChange={(e) =>
                                            setUsername(e.target.value)
                                        }
                                    />
                                    <Input
                                        name="password"
                                        type="password"
                                        label="Contraseña"
                                        placeholder="Ex.: 123456"
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(e.target.value)
                                        }
                                    />
                                </div>
                                <div className="login-form__options">
                                    <InputCheckbox
                                        name="rememberMe"
                                        id="rememberMe"
                                        label="Mantener sesión iniciada"
                                        spacing="4px"
                                        checked={rememberMe}
                                        onChange={(e) => {
                                            console.log(
                                                "Checkbox clicked:",
                                                e.target.checked
                                            );
                                            setRememberMe(e.target.checked);
                                        }}
                                    />
                                </div>
                                {error && (
                                    <div className="login-form__error">
                                        {error}
                                    </div>
                                )}
                                <Button
                                    variant="filled"
                                    fullWidth={true}
                                    type="submit"
                                >
                                    Iniciar sesión
                                </Button>
                                <a
                                    href="/forgot-password"
                                    className="login-form__forgot"
                                >
                                    ¿Olvidaste tu contraseña?
                                </a>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Login;
